import { Link } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useSyncExternalStore } from 'react';

/** Bottom-left toast, auto-dismiss after 6 s, optional link to the saved record (document 05). */
interface ToastItem {
  id: number;
  message: string;
  tone: 'success' | 'error';
  link?: { to: string; label: string };
}

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function toast(
  message: string,
  opts: { tone?: ToastItem['tone']; link?: ToastItem['link'] } = {},
): void {
  const id = ++seq;
  items = [
    ...items,
    { id, message, tone: opts.tone ?? 'success', ...(opts.link ? { link: opts.link } : {}) },
  ];
  emit();
  setTimeout(() => dismiss(id), 6000);
}

function dismiss(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

export function Toaster() {
  const list = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => items,
  );
  return (
    <div
      className="pointer-events-none fixed bottom-20 left-4 z-[100] flex flex-col gap-2"
      aria-live="polite"
    >
      {list.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`pointer-events-auto flex min-w-[280px] max-w-md items-start gap-3 rounded-lg px-4 py-3 text-sm text-white shadow-lg ${
            t.tone === 'error' ? 'bg-destructive' : 'bg-foreground'
          }`}
        >
          <span className="flex-1">
            {t.message}
            {t.link ? (
              <>
                {' '}
                <Link to={t.link.to} className="font-medium underline">
                  {t.link.label}
                </Link>
              </>
            ) : null}
          </span>
          <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
            <X className="size-4 opacity-70" />
          </button>
        </div>
      ))}
    </div>
  );
}
